import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useReducer, useState } from 'react';
import type { TableCellHeaderSortOptions } from 'app/components';
import { useDialog } from 'app/hooks';
import { useEmployersSearchQuery } from 'app/hooks/api';
import { useStoreSelector } from 'app/store';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

type QueryState = {
  page: number;
  pageSize: number;
  searchQuery: string;
  debouncedSearchQuery: string;
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc' | undefined;
};

type QueryAction =
  | { type: 'SEARCH'; query: string }
  | { type: 'DEBOUNCE_SETTLED'; query: string }
  | { type: 'PAGE_CHANGE'; page: number }
  | { type: 'PAGE_SIZE_CHANGE'; pageSize: number }
  | { type: 'SORT_CHANGE'; sortBy: string | undefined; sortOrder: 'asc' | 'desc' | undefined };

const initialQueryState: QueryState = {
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  searchQuery: '',
  debouncedSearchQuery: '',
  sortBy: undefined,
  sortOrder: undefined,
};

const queryReducer = (state: QueryState, action: QueryAction): QueryState => {
  switch (action.type) {
    case 'SEARCH':
      return { ...state, searchQuery: action.query };
    case 'DEBOUNCE_SETTLED':
      return { ...state, debouncedSearchQuery: action.query, page: DEFAULT_PAGE };
    case 'PAGE_CHANGE':
      return { ...state, page: action.page };
    case 'PAGE_SIZE_CHANGE':
      return { ...state, pageSize: action.pageSize, page: DEFAULT_PAGE };
    case 'SORT_CHANGE':
      return { ...state, sortBy: action.sortBy, sortOrder: action.sortOrder, page: DEFAULT_PAGE };
    default:
      return state;
  }
};

export const useEmployerManagementPage = () => {
  const [createEmployerModalOpen, setCreateEmployerModalOpen] = useState(false);
  const [queryState, dispatch] = useReducer(queryReducer, initialQueryState);
  const { page, pageSize, searchQuery, debouncedSearchQuery, sortBy, sortOrder } = queryState;

  const ownerId = useStoreSelector(state => state.AppReducer.environment.ownerId);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: 'DEBOUNCE_SETTLED', query: searchQuery });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isFetching } = useEmployersSearchQuery({
    variables: { page, pageSize, searchQuery: debouncedSearchQuery, sortBy, sortOrder, ownerId },
  });

  const {
    dialogState: showEmployerUploadFormDialog,
    openDialog: openEmployerUploadFormDialog,
    closeDialog: closeEmployerUploadFormDialog,
  } = useDialog();

  const handleCloseEmployerUploadFormDialog = useCallback(() => {
    closeEmployerUploadFormDialog();
  }, [closeEmployerUploadFormDialog]);

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SEARCH', query: event.target.value });
  }, []);

  const handlePageChange = useCallback(({ rowSize, page: newPage }: { page?: number; rowSize?: number }) => {
    if (rowSize !== undefined) {
      dispatch({ type: 'PAGE_SIZE_CHANGE', pageSize: rowSize });
    }
    if (newPage !== undefined) {
      dispatch({ type: 'PAGE_CHANGE', page: newPage });
    }
  }, []);

  const handleSortChange = useCallback((sortByRecord: Record<string, TableCellHeaderSortOptions>) => {
    const column = Object.keys(sortByRecord).find(key => sortByRecord[key] !== null);
    const direction = column ? sortByRecord[column] : null;
    dispatch({ type: 'SORT_CHANGE', sortBy: direction ? column : undefined, sortOrder: direction ?? undefined });
  }, []);

  const openCreateEmployerModal = useCallback(() => setCreateEmployerModalOpen(true), []);
  const closeCreateEmployerModal = useCallback(() => setCreateEmployerModalOpen(false), []);

  return {
    employers: data?.items ?? [],
    totalItems: data?.totalItems ?? 0,
    isFetching,

    page,
    pageSize,
    searchQuery,

    createEmployerModalOpen,
    showEmployerUploadFormDialog,

    handleSearch,
    handlePageChange,
    handleSortChange,
    openCreateEmployerModal,
    closeCreateEmployerModal,
    openEmployerUploadFormDialog,
    closeEmployerUploadFormDialog: handleCloseEmployerUploadFormDialog,
  };
};
